import { useState, useRef, useEffect, useCallback } from "react";
import { Icon } from "@/components/ui/icon";
import { Button } from "@/components/ui/button";
import { cn } from "@/lib/utils";
import { supabase } from "@/integrations/supabase/client";
import { OttoHeader, OttoMessageList, OttoInput, Message } from "@/components/otto";
import {
  OTTO_HISTORY_MESSAGE_LIMIT,
  OTTO_TITLE_TRUNCATE_LENGTH,
  OTTO_WELCOME_MESSAGE
} from "@/lib/constants";

interface OttoChatBubbleProps {
  conversationId?: string | null;
  onConversationCreated?: (id: string) => void;
  isEmbedded?: boolean;
}

const createWelcomeMessage = (): Message => ({
  id: "welcome",
  role: "assistant",
  content: OTTO_WELCOME_MESSAGE,
});

const OttoChatBubble = ({
  conversationId: externalConversationId,
  onConversationCreated,
  isEmbedded = false
}: OttoChatBubbleProps) => {
  const [isOpen, setIsOpen] = useState(isEmbedded);
  const [conversationId, setConversationId] = useState<string | null>(null);
  const lastLoadedConversationRef = useRef<string | null>(null);
  const [messages, setMessages] = useState<Message[]>([createWelcomeMessage()]);
  const [input, setInput] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const [isLoadingHistory, setIsLoadingHistory] = useState(false);
  const scrollRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);

  // Load conversation history
  const loadConversation = useCallback(async (convId: string) => {
    setIsLoadingHistory(true);
    try {
      const { data, error } = await supabase
        .from("otto_messages")
        .select("id, role, content")
        .eq("conversation_id", convId)
        .order("created_at", { ascending: true });

      if (error) throw error;

      if (data && data.length > 0) {
        setMessages(data.map(msg => ({
          id: msg.id,
          role: msg.role as "user" | "assistant",
          content: msg.content,
        })));
      } else {
        setMessages([createWelcomeMessage()]);
      }
      setConversationId(convId);
      lastLoadedConversationRef.current = convId;
    } catch (error) {
      console.error("Error loading conversation:", error);
    } finally {
      setIsLoadingHistory(false);
    }
  }, []);

  // Load external conversation when prop changes - fix for hasLoadedRef bug
  useEffect(() => {
    if (externalConversationId && externalConversationId !== lastLoadedConversationRef.current) {
      loadConversation(externalConversationId);
      if (isEmbedded) setIsOpen(true);
    }
  }, [externalConversationId, loadConversation, isEmbedded]);

  useEffect(() => {
    if (scrollRef.current) {
      scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
    }
  }, [messages]);

  useEffect(() => {
    if (isOpen && inputRef.current) {
      inputRef.current.focus();
    }
  }, [isOpen]);

  const createConversation = async (firstMessage: string): Promise<string | null> => {
    try {
      const { data: userData } = await supabase.auth.getUser();
      if (!userData.user) return null;

      const title = firstMessage.substring(0, OTTO_TITLE_TRUNCATE_LENGTH) +
        (firstMessage.length > OTTO_TITLE_TRUNCATE_LENGTH ? "..." : "");

      const { data, error } = await supabase
        .from("otto_conversations")
        .insert({
          user_id: userData.user.id,
          title,
        })
        .select("id")
        .single();

      if (error) throw error;

      onConversationCreated?.(data.id);
      return data.id;
    } catch (error) {
      console.error("Error creating conversation:", error);
      return null;
    }
  };

  const saveMessage = async (convId: string, role: "user" | "assistant", content: string) => {
    try {
      await supabase.from("otto_messages").insert({
        conversation_id: convId,
        role,
        content,
      });

      // Update conversation updated_at
      await supabase
        .from("otto_conversations")
        .update({ updated_at: new Date().toISOString() })
        .eq("id", convId);
    } catch (error) {
      console.error("Error saving message:", error);
    }
  };

  const startNewConversation = () => {
    setConversationId(null);
    lastLoadedConversationRef.current = null;
    setMessages([createWelcomeMessage()]);
    setInput("");
  };

  const sendMessage = async () => {
    if (!input.trim() || isLoading) return;

    const userMessage: Message = {
      id: Date.now().toString(),
      role: "user",
      content: input.trim(),
    };

    setMessages((prev) => [...prev, userMessage]);
    setInput("");
    setIsLoading(true);

    let currentConvId = conversationId;

    // Create conversation if this is the first message
    if (!currentConvId) {
      currentConvId = await createConversation(userMessage.content);
      if (currentConvId) {
        setConversationId(currentConvId);
        lastLoadedConversationRef.current = currentConvId;
        // Save the welcome message first
        await saveMessage(currentConvId, "assistant", messages[0].content);
      }
    }

    // Save user message
    if (currentConvId) {
      await saveMessage(currentConvId, "user", userMessage.content);
    }

    try {
      // Build conversation history for context
      const currentMessages = [...messages, userMessage];
      const historyMessages = currentMessages.slice(-OTTO_HISTORY_MESSAGE_LIMIT).map(msg => ({
        role: msg.role,
        content: msg.content,
      }));

      const { data, error } = await supabase.functions.invoke("otto-chat", {
        body: {
          message: userMessage.content,
          history: historyMessages,
        },
      });

      if (error) throw error;

      const assistantContent = data.response || "I couldn't process that request. Please try again.";
      const assistantMessage: Message = {
        id: (Date.now() + 1).toString(),
        role: "assistant",
        content: assistantContent,
      };

      setMessages((prev) => [...prev, assistantMessage]);

      // Save assistant response
      if (currentConvId) {
        await saveMessage(currentConvId, "assistant", assistantContent);
      }
    } catch (error) {
      console.error("Error sending message:", error);
      const errorMessage: Message = {
        id: (Date.now() + 1).toString(),
        role: "assistant",
        content: "Sorry, I encountered an error. Please try again.",
      };
      setMessages((prev) => [...prev, errorMessage]);

      if (currentConvId) {
        await saveMessage(currentConvId, "assistant", errorMessage.content);
      }
    } finally {
      setIsLoading(false);
    }
  };

  const isInputDisabled = isLoading || isLoadingHistory;

  // For embedded mode, render without the floating button
  if (isEmbedded) {
    return (
      <div className="h-full flex flex-col bg-background border border-border rounded-lg overflow-hidden">
        <OttoHeader onNewConversation={startNewConversation} />
        <OttoMessageList
          ref={scrollRef}
          messages={messages}
          isLoading={isLoading}
          isLoadingHistory={isLoadingHistory}
        />
        <OttoInput
          ref={inputRef}
          value={input}
          onChange={setInput}
          onSend={sendMessage}
          disabled={isInputDisabled}
        />
      </div>
    );
  }

  return (
    <div className="fixed bottom-4 right-4 z-50 max-md:bottom-0 max-md:right-0 max-md:left-0 max-md:top-0 max-md:pointer-events-none">
      {/* Chat Window */}
      {isOpen && (
        <div className="absolute bottom-16 right-0 w-80 sm:w-96 h-[500px] bg-background border border-border rounded-lg shadow-xl flex flex-col overflow-hidden animate-in slide-in-from-bottom-2 duration-200 max-md:fixed max-md:inset-0 max-md:w-full max-md:h-full max-md:bottom-0 max-md:rounded-none max-md:border-0 max-md:pointer-events-auto">
          <OttoHeader
            onNewConversation={startNewConversation}
            onClose={() => setIsOpen(false)}
            showCloseButton
          />
          <OttoMessageList
            ref={scrollRef}
            messages={messages}
            isLoading={isLoading}
            isLoadingHistory={isLoadingHistory}
          />
          <OttoInput
            ref={inputRef}
            value={input}
            onChange={setInput}
            onSend={sendMessage}
            disabled={isInputDisabled}
          />
        </div>
      )}

      {/* Floating Button */}
      <Button
        onClick={() => setIsOpen(!isOpen)}
        size="icon"
        className={cn(
          "h-14 w-14 rounded-full shadow-lg transition-transform hover:scale-105 pointer-events-auto",
          "max-md:fixed max-md:bottom-4 max-md:right-4",
          isOpen && "max-md:hidden"
        )}
      >
        {isOpen ? (
          <Icon name="close" size={24} />
        ) : (
          <Icon name="chat" size={24} />
        )}
      </Button>
    </div>
  );
};

export default OttoChatBubble;
